import { getOrgLanguageCodes, translateBackend } from '@assets/i18n/config';
import MugImage from '@assets/images/custom/mug.svg';
import FormModal from '@components/modals/formModal';
import type { IncompleteFormAgreement } from '@interfaces/formAgreements';
import {
  CheckCircle,
  Close,
  Download,
  type SvgIconComponent,
  Upload,
  Warning,
} from '@mui/icons-material';
import {
  CircularProgress,
  ListItem,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { Jurisdictions, LanguageCodes } from '@onevesthq/ov-enums';
import { colors } from '@ovComponents/0-tokens/ref/palette/palette';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  RefDialogContent,
} from '@ovComponents/2-component/dialog/dialog';
import { FileTypes } from '@ovComponents/2-component/droppableFileInput/fileTypes';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { PdfViewer } from '@ovComponents/2-component/pdfViewer/pdfViewer';
import type {
  Segment,
  SegmentValue,
} from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { SegmentedControl } from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { StatusTag } from '@ovComponents/2-component/statusTag/statusTag';
import DroppableFileInput from '@pages/client/components/droppableFileInput';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { downloadHtmlAsPdf } from '@utils/downloadHtmlAsPdf';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type SignableDocumentState = IncompleteFormAgreement & {
  signed: boolean;
  pending?: boolean;
};

export type UploadableDocumentState = IncompleteFormAgreement & {
  pending?: boolean;
  uploaded: boolean;
};

export const ACCEPTED_TYPES = [
  FileTypes.PDF,
  FileTypes.PNG,
  FileTypes.JPG,
  FileTypes.JPEG,
  FileTypes.CSV,
];

const SignableDocumentStatusTag = ({
  document,
  requireSigning,
  t,
}: {
  document: SignableDocumentState;
  requireSigning: boolean;
  t: (key: string) => string;
}) => {
  if (document.pending) {
    return (
      <StatusTag
        color='informative'
        icon={CircularProgress as SvgIconComponent}
        label={t('pending')}
      />
    );
  }

  const signedLabelKey = requireSigning ? 'signed' : 'reviewed';
  const unsignedLabelKey = requireSigning ? 'notSigned' : 'notReviewed';

  return (
    <StatusTag
      color={document.signed ? 'positive' : 'warning'}
      icon={document.signed ? CheckCircle : Warning}
      label={t(document.signed ? signedLabelKey : unsignedLabelKey)}
    />
  );
};

export const DocumentsVisual = ({
  options,
  signableDocuments,
  uploadableDocuments,
  loading,
  previewLoading,
  signFormAgreementPending,
  signAgreement,
  doTheUpload,
  doTheUploadForSignable,
  canManuallyUploadFormAgreement,
  onUploadSignedDocumentClick,
  continueFunc,
  downloadTemplatePDF,
  uploadInProgress,
  signableDocumentIndex,
  setSignableDocumentIndex,
  uploadableDocumentIndex,
  setUploadableDocumentIndex,
  html,
  pdfUrl,
  viewNext,
  signAll,
  userJurisdiction,
  languageAction,
  updateLanguageAction,
}: {
  options: any;
  signableDocuments: SignableDocumentState[];
  uploadableDocuments: UploadableDocumentState[];
  loading: boolean;
  previewLoading: boolean;
  signFormAgreementPending?: boolean;
  signAgreement: () => void;
  doTheUpload: (file: File, name: string) => void;
  doTheUploadForSignable: (file: File, name: string) => void;
  canManuallyUploadFormAgreement: boolean;
  onUploadSignedDocumentClick: () => void;
  continueFunc: () => void;
  downloadTemplatePDF: (type: 'signable' | 'uploadable') => void;
  uploadInProgress?: boolean;
  signableDocumentIndex?: number;
  setSignableDocumentIndex: (newIndex?: number) => void;
  uploadableDocumentIndex?: number;
  setUploadableDocumentIndex: (newIndex?: number) => void;
  html: string;
  pdfUrl?: string;
  viewNext: () => void;
  signAll: () => Promise<void>;
  userJurisdiction?: string;
  languageAction?: SegmentValue;
  updateLanguageAction: (value: SegmentValue) => void;
}) => {
  const { t } = useTranslation('client');
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [signableUploadModalOpen, setSignableUploadModalOpen] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [failedPdf, setFailedPdf] = useState(false);

  const scrollToTheTop = () => {
    if (ref && ref.current) ref?.current?.scrollTo(0, 0);
  };

  const onPdfError = () => {
    setLoadingPdf(false);
    setFailedPdf(true);
  };
  const onPdfFulfilled = () => {
    setLoadingPdf(false);
    setFailedPdf(false);
  };

  const numUnsignedDocuments = signableDocuments.filter(
    (x) => !x.signed,
  ).length;
  const numUnuploadedDocuments = uploadableDocuments.filter(
    (x) => !x.uploaded,
  ).length;

  const signAllAndContinue = async () => {
    sendAnalytic(ovAnalyticsEvents.workflowAgreementsContinueButtonSelect);
    await signAll();
    continueFunc();
  };

  const BorderlessTableCell = styled(TableCell)({ border: 'none' });
  const OverlineTypography = styled(Typography)({
    textTransform: 'uppercase',
    color: colors.neutral600,
  });
  const isQCJurisdiction: boolean = userJurisdiction === Jurisdictions.CA_QC;

  const DocumentTableHeader = () => (
    <TableHead>
      <TableRow>
        <TableCell sx={{ width: '100%' }}>
          <OverlineTypography variant='labelSmall'>
            {t('document')}
          </OverlineTypography>
        </TableCell>
        <TableCell sx={{ minWidth: '140px' }}>
          <OverlineTypography variant='labelSmall'>
            {t('status')}
          </OverlineTypography>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const getDocumentText = (amountOfDocument: number): string => {
    if (amountOfDocument === 0) return t('continue');
    if (amountOfDocument === 1) return t('signAndContinue');
    return t('signAllAndContinue');
  };

  const segments: Segment[] = getOrgLanguageCodes().map((langCode) => ({
    value: langCode,
    label: langCode.toUpperCase(),
  }));

  return (
    <>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      {loading && !signFormAgreementPending && (
        <Skeleton width='100%' height='400px' variant='rectangular' />
      )}
      {!loading &&
        signableDocuments.length === 0 &&
        uploadableDocuments.length === 0 && (
          <Box display='flex' alignItems='center' flexDirection='column'>
            <img src={MugImage} alt='mug-icon' />
            <Typography
              data-testid='nothing-to-sign'
              variant='bodyLarge'
              sx={{
                mt: 1,
                maxWidth: '500px',
                textAlign: 'center',
                mb: 3,
              }}
            >
              {t('nothingToSign')}
            </Typography>
          </Box>
        )}

      {signableDocuments.length > 0 && (
        <>
          <Table sx={{ mb: 3 }}>
            <DocumentTableHeader />
            <TableBody data-testid='documents-table'>
              {signableDocuments.map((document, index) => (
                <TableRow
                  data-testid={`document-${index}`}
                  key={index}
                  hover
                  sx={{ cursor: document.pending ? 'default' : 'pointer' }}
                  onClick={() => {
                    if (document.pending) return;
                    setSignableDocumentIndex(index);
                    if (document.isPrefilledPdfEnabled) {
                      setLoadingPdf(true);
                      setFailedPdf(false);
                    }
                  }}
                >
                  <BorderlessTableCell>
                    {translateBackend(document.translatedDisplayName)}
                  </BorderlessTableCell>
                  <BorderlessTableCell>
                    <SignableDocumentStatusTag
                      document={document}
                      requireSigning={options.requireSigning}
                      t={t}
                    />
                  </BorderlessTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {options.allowBulk &&
            options.requireSigning &&
            numUnuploadedDocuments > 0 && (
              <Box display='flex' justifyContent='end'>
                <Button
                  dataTestId='documents-sign-all-button'
                  label={t('signAll')}
                  disabled={numUnsignedDocuments === 0}
                  onClick={signAll}
                />
              </Box>
            )}
        </>
      )}

      {uploadableDocuments && uploadableDocuments.length > 0 && (
        <Table sx={{ mt: 2, mb: 3 }}>
          <DocumentTableHeader />
          <TableBody>
            {uploadableDocuments.map((document, index) => (
              <TableRow
                data-testid={`document-${index}`}
                key={index}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  if (!document.pending) {
                    setUploadableDocumentIndex(index);
                  }
                }}
              >
                <BorderlessTableCell>
                  {translateBackend(document.translatedDisplayName)}
                </BorderlessTableCell>
                <BorderlessTableCell>
                  {document.pending ? (
                    <StatusTag
                      color='informative'
                      icon={CircularProgress as SvgIconComponent}
                      label={t('pending')}
                    />
                  ) : (
                    <StatusTag
                      color={document.uploaded ? 'positive' : 'warning'}
                      icon={document.uploaded ? CheckCircle : Warning}
                      label={
                        document.uploaded
                          ? t(options.requireSigning ? 'uploaded' : 'reviewed')
                          : t(options.requireSigning ? 'upload' : 'notReviewed')
                      }
                    />
                  )}
                </BorderlessTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Box display='flex' justifyContent='end' sx={{ mt: 7 }}>
        {options.allowBulk &&
        options.requireSigning &&
        numUnuploadedDocuments === 0 ? (
          <Button
            dataTestId='sign-and-continue-button'
            label={getDocumentText(numUnsignedDocuments)}
            disabled={loading}
            onClick={signAllAndContinue}
          />
        ) : (
          <Button
            dataTestId='continue-btn'
            label={t('continue')}
            disabled={
              loading ||
              (options.requireSigning &&
                (numUnsignedDocuments > 0 || numUnuploadedDocuments > 0))
            }
            type='submit'
            onClick={continueFunc}
          />
        )}
      </Box>

      {signableDocumentIndex !== undefined && (
        <Dialog
          open={true}
          fullWidth
          maxWidth='lg'
          onClose={() => setSignableDocumentIndex(undefined)}
        >
          <DialogTitle>
            <Box
              display='flex'
              width='100%'
              justifyContent='space-between'
              alignItems='center'
            >
              <Box>
                {translateBackend(
                  signableDocuments[signableDocumentIndex]
                    .translatedDisplayName,
                )}
              </Box>
              <Box display='flex' alignItems='center'>
                {isQCJurisdiction && (
                  <SegmentedControl
                    value={languageAction}
                    segments={segments}
                    exclusive
                    enforceActive
                    onChange={updateLanguageAction}
                  />
                )}
                <IconButton
                  data-testid='close-btn'
                  onClick={() => {
                    setSignableDocumentIndex(undefined);
                    if (isQCJurisdiction)
                      updateLanguageAction(LanguageCodes.fr);
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <RefDialogContent ref={ref}>
            <Box height='100%' overflow='auto'>
              {previewLoading && (
                <Box display='flex' justifyContent='center' width='552px'>
                  <CircularProgress />
                </Box>
              )}
              {html && (
                <Typography sx={{ lineHeight: 'inherit' }}>
                  <span
                    className='content'
                    dangerouslySetInnerHTML={{
                      __html: html,
                    }}
                  ></span>
                </Typography>
              )}
              {!html && pdfUrl && (
                <PdfViewer
                  pdfUrl={pdfUrl}
                  onError={onPdfError}
                  onFulfilled={onPdfFulfilled}
                  fullWidth
                />
              )}
            </Box>
          </RefDialogContent>
          <DialogFooter>
            <Box display='flex' justifyContent='flex-end' m={1}>
              {signableDocumentIndex === signableDocuments.length - 1 ? (
                !signableDocuments[signableDocumentIndex].signed ? (
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    gap={2}
                    width='100%'
                  >
                    {pdfUrl && (
                      <Button
                        dataTestId='download-template-btn'
                        variant='outlined'
                        label={t('client:downloadTemplate')}
                        onClick={() => downloadTemplatePDF('signable')}
                        disabled={uploadInProgress}
                      />
                    )}
                    {html && (
                      <Button
                        variant='outlined'
                        dataTestId='download-html-as-pdf-btn'
                        leadingIcon={Download}
                        label={t('client:documents.downloadHtmlAsPdf')}
                        onClick={() => downloadHtmlAsPdf(html)}
                        disabled={uploadInProgress}
                      />
                    )}
                    {canManuallyUploadFormAgreement && (
                      <Button
                        dataTestId='upload-signed-document-btn'
                        variant='outlined'
                        leadingIcon={Upload}
                        label={t('client:documents.uploadSignedDocument')}
                        onClick={() => {
                          onUploadSignedDocumentClick();
                          setSignableUploadModalOpen(true);
                        }}
                        disabled={uploadInProgress}
                      />
                    )}
                    <Button
                      dataTestId='preview-sign-btn'
                      disabled={previewLoading || loadingPdf || failedPdf}
                      label={t(options.requireSigning ? 'sign' : 'review')}
                      onClick={() => {
                        signAgreement();
                        setSignableDocumentIndex(undefined);
                      }}
                    />
                  </Box>
                ) : (
                  <Button
                    dataTestId='download-template-done-button'
                    disabled={previewLoading}
                    label={t('done')}
                    onClick={() => {
                      viewNext();
                      setSignableDocumentIndex(undefined);
                    }}
                  />
                )
              ) : !signableDocuments[signableDocumentIndex].signed ? (
                <Box
                  display='flex'
                  justifyContent='space-between'
                  gap={2}
                  width='100%'
                >
                  {pdfUrl && (
                    <Button
                      variant='outlined'
                      dataTestId='download-template-btn'
                      label={t('client:downloadTemplate')}
                      onClick={() => downloadTemplatePDF('signable')}
                      disabled={uploadInProgress}
                    />
                  )}
                  {html && (
                    <Button
                      variant='outlined'
                      dataTestId='download-html-as-pdf-btn'
                      leadingIcon={Download}
                      label={t('client:documents.downloadHtmlAsPdf')}
                      onClick={() => downloadHtmlAsPdf(html)}
                      disabled={uploadInProgress}
                    />
                  )}
                  {canManuallyUploadFormAgreement && (
                    <Button
                      dataTestId='upload-signed-document-btn'
                      variant='outlined'
                      leadingIcon={Upload}
                      label={t('client:documents.uploadSignedDocument')}
                      onClick={() => {
                        onUploadSignedDocumentClick();
                        setSignableUploadModalOpen(true);
                      }}
                      disabled={uploadInProgress}
                    />
                  )}
                  <Button
                    dataTestId='action-view-next-btn'
                    disabled={previewLoading || loadingPdf || failedPdf}
                    label={t(
                      options.requireSigning ? 'signAndViewNext' : 'reviewNext',
                    )}
                    onClick={() => {
                      scrollToTheTop();
                      signAgreement();
                      if (isQCJurisdiction)
                        updateLanguageAction(LanguageCodes.fr);
                    }}
                  />
                </Box>
              ) : (
                <Button
                  dataTestId='view-next-btn'
                  disabled={previewLoading}
                  label={t('viewNext')}
                  onClick={() => {
                    scrollToTheTop();
                    viewNext();
                  }}
                />
              )}
            </Box>
          </DialogFooter>
        </Dialog>
      )}

      {signableUploadModalOpen && signableDocumentIndex !== undefined && (
        <DocumentUpload
          documentName={translateBackend(
            signableDocuments[signableDocumentIndex].translatedDisplayName,
          )}
          doTheUpload={doTheUploadForSignable}
          uploadInProgress={uploadInProgress}
          handleClose={() => setSignableUploadModalOpen(false)}
        />
      )}

      {uploadableDocumentIndex !== undefined && uploadableDocuments && (
        <>
          <Dialog
            open={true}
            onClose={() => setUploadableDocumentIndex(undefined)}
          >
            <DialogTitle>
              {translateBackend(
                uploadableDocuments[uploadableDocumentIndex]
                  .translatedDisplayName,
              )}
            </DialogTitle>
            <DialogContent>
              <Box width='550px' display='flex' justifyContent='center'>
                {!uploadableDocuments[uploadableDocumentIndex].uploaded ? (
                  <Typography m={5}>
                    {t('documents.cantSignDigitallyPleaseUpload')}
                  </Typography>
                ) : (
                  <Typography m={5}>
                    {t('documents.documentHasBeenUploaded')}
                  </Typography>
                )}
              </Box>
              <Stack spacing={2}>
                {uploadableDocuments[uploadableDocumentIndex].templateUrl && (
                  <Button
                    fullWidth
                    variant='outlined'
                    dataTestId='download-template-btn'
                    label={t('client:downloadTemplate')}
                    onClick={() => downloadTemplatePDF('uploadable')}
                    disabled={uploadInProgress}
                  />
                )}
                {!uploadableDocuments[uploadableDocumentIndex].uploaded ? (
                  <Button
                    fullWidth
                    dataTestId='upload-signed-btn'
                    label={t(
                      options.requireSigning ? 'uploadSigned' : 'review',
                    )}
                    onClick={() => {
                      setUploadModalOpen(true);
                    }}
                  />
                ) : (
                  <Button
                    dataTestId='upload-done-button'
                    fullWidth
                    label={t('done')}
                    onClick={() => {
                      setUploadableDocumentIndex(undefined);
                    }}
                  />
                )}
              </Stack>
            </DialogContent>
          </Dialog>
          {uploadModalOpen && (
            <DocumentUpload
              documentName={translateBackend(
                uploadableDocuments[uploadableDocumentIndex]
                  .translatedDisplayName,
              )}
              doTheUpload={doTheUpload}
              uploadInProgress={uploadInProgress}
              handleClose={() => setUploadModalOpen(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default DocumentsVisual;

const DocumentUpload = ({
  documentName,
  doTheUpload,
  uploadInProgress,
  handleClose,
}: {
  documentName: string;
  doTheUpload: (file: File, name: string) => void;
  uploadInProgress?: boolean;
  handleClose: () => void;
}) => {
  const [file, setFile] = useState<File>();
  const [name, setName] = useState('');
  const [nameIsAutomatic, setNameIsAutomatic] = useState(false);

  const { t } = useTranslation('client');

  useEffect(() => {
    if (file?.name) {
      if (!name || nameIsAutomatic) {
        setName(file.name);
        setNameIsAutomatic(true);
      }
    } else {
      setName('');
    }
  }, [file]);

  return (
    <FormModal
      onSubmit={async (event) => {
        event.preventDefault();
        if (!file) return;
        await doTheUpload(file, name);
        handleClose();
      }}
      loading={false}
      title={`${documentName} ${t('documentUpload.documentUpload')}`}
      open={true}
      handleClose={handleClose}
      formButton={'upload document'}
      disabled={!file || !name || uploadInProgress}
    >
      <ListItem>
        <DroppableFileInput
          acceptedTypes={ACCEPTED_TYPES}
          onFileChosen={(dropFile: File) => setFile(dropFile)}
        />
      </ListItem>
      <ListItem>
        <TextField
          fullWidth
          label={t('documentUpload.documentName')}
          value={name}
          onChange={(e) => {
            setName(e.target.value.trim());
            setNameIsAutomatic(false);
          }}
        />
      </ListItem>
    </FormModal>
  );
};
